<template>
    <div>
        <v-row dense class="mb-2">
            <v-col cols="12" sm="4">
                <v-select v-model="statusFilter" :items="statusItems" label="Status" outlined dense hide-details clearable />
            </v-col>
            <v-col cols="12" sm="8" class="d-flex align-center">
                <span class="caption grey--text">Submitter and company are only visible to admins.</span>
                <v-spacer />
                <v-btn small text color="primary" :loading="loading" @click="load"><v-icon small left>mdi-refresh</v-icon>Refresh</v-btn>
            </v-col>
        </v-row>

        <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>

        <v-data-table :headers="headers" :items="filteredItems" :loading="loading" item-key="id" :items-per-page="15" dense class="elevation-0">
            <template v-slot:item.title="{ item }">
                <div style="max-width: 360px">
                    <div class="font-weight-medium text-truncate">{{ item.title }}</div>
                    <div class="caption grey--text text-truncate">{{ item.description }}</div>
                    <div v-if="item.route_name || item.element_hint" class="caption grey--text text-truncate">
                        <v-icon x-small>mdi-map-marker-outline</v-icon> {{ [item.route_name, item.element_hint].filter(Boolean).join(" · ") }}
                    </div>
                    <div v-if="item.attachment_count" class="caption grey--text">
                        <v-icon x-small>mdi-image-outline</v-icon> {{ item.attachment_count }} {{ item.attachment_count === 1 ? "screenshot" : "screenshots" }}
                    </div>
                </div>
            </template>
            <template v-slot:item.type="{ item }">
                <v-chip x-small label :color="typeMeta(item.type).color" text-color="white">{{ typeMeta(item.type).label }}</v-chip>
            </template>
            <template v-slot:item.status="{ item }">
                <v-chip x-small label outlined :color="statusMeta(item.status).color">{{ statusMeta(item.status).label }}</v-chip>
            </template>
            <template v-slot:item.company_name="{ item }">
                <div>{{ item.company_name || "Company " + item.company_id }}</div>
                <div class="caption grey--text">{{ item.username }}</div>
            </template>
            <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
            <template v-slot:item.actions="{ item }">
                <div class="d-flex justify-end" style="white-space: nowrap">
                    <v-btn v-if="item.status === 'pending'" icon small color="green darken-1" title="Approve" aria-label="Approve" @click="setStatus(item, 'open')"><v-icon small>mdi-check</v-icon></v-btn>
                    <v-btn v-if="item.status !== 'merged'" icon small color="red darken-1" title="Reject" aria-label="Reject" @click="openReject(item)"><v-icon small>mdi-close</v-icon></v-btn>
                    <v-menu v-if="item.status !== 'merged'" offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon small v-bind="attrs" v-on="on" title="Set status" aria-label="Set status"><v-icon small>mdi-flag-outline</v-icon></v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item v-for="status in settableStatuses" :key="status" :disabled="item.status === status" @click="setStatus(item, status)">
                                <v-list-item-title>{{ statusMeta(status).label }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-btn icon small title="Edit" aria-label="Edit" @click="openEdit(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
                    <v-btn v-if="item.status !== 'merged'" icon small title="Merge into another item" aria-label="Merge" @click="openMerge(item)"><v-icon small>mdi-call-merge</v-icon></v-btn>
                    <v-btn icon small title="Details and votes by company" aria-label="Details and votes by company" @click="$emit('show-votes', item)"><v-icon small>mdi-domain</v-icon></v-btn>
                </div>
            </template>
        </v-data-table>

        <!-- Reject: Begruendung ist Pflicht und wird oeffentlich angezeigt -->
        <v-dialog v-model="rejectDialog" max-width="480">
            <v-card>
                <v-card-title>Reject feedback</v-card-title>
                <v-card-text>
                    <p class="mb-3">
                        <strong>{{ target ? target.title : "" }}</strong>
                    </p>
                    <v-textarea v-model="reason" label="Reason (shown publicly)" rows="3" auto-grow outlined counter="2000" maxlength="2000" :rules="[(v) => !!(v && v.trim()) || 'A reason is required']" />
                    <v-alert v-if="dialogError" type="error" dense outlined class="mb-0">{{ dialogError }}</v-alert>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text rounded @click="rejectDialog = false">Cancel</v-btn>
                    <v-btn class="ml-auto" rounded color="primary" :loading="saving" :disabled="!reason.trim()" @click="confirmReject">Reject</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Edit: Text redigieren und oeffentliche Antwort pflegen -->
        <v-dialog v-model="editDialog" max-width="560" scrollable>
            <v-card>
                <v-card-title>Edit feedback</v-card-title>
                <v-card-text>
                    <v-select v-model="edit.type" :items="typeItems" label="Type" outlined dense />
                    <v-select v-model="edit.suite" :items="suiteItems" label="Suite" outlined dense clearable />
                    <v-text-field v-model="edit.title" label="Title" maxlength="160" counter="160" outlined dense />
                    <v-textarea v-model="edit.description" label="Description" rows="4" auto-grow outlined maxlength="5000" counter="5000" />
                    <v-textarea v-model="edit.admin_note" label="Reply from Quaro (shown publicly)" rows="2" auto-grow outlined maxlength="2000" counter="2000" />
                    <v-alert v-if="dialogError" type="error" dense outlined class="mb-0">{{ dialogError }}</v-alert>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text rounded @click="editDialog = false">Cancel</v-btn>
                    <v-btn class="ml-auto" rounded color="primary" :loading="saving" :disabled="!edit.title.trim() || !edit.description.trim()" @click="confirmEdit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Merge: Duplikat auf einen anderen Beitrag zusammenfuehren -->
        <v-dialog v-model="mergeDialog" max-width="520">
            <v-card>
                <v-card-title>Merge into another item</v-card-title>
                <v-card-text>
                    <p class="mb-3">
                        <strong>{{ target ? target.title : "" }}</strong> will be hidden. Its votes count towards the item you pick (every user counts once).
                    </p>
                    <v-autocomplete v-model="mergeTargetId" :items="mergeCandidates" item-text="title" item-value="id" label="Merge into" outlined dense no-data-text="No matching item" />
                    <v-alert v-if="dialogError" type="error" dense outlined class="mb-0">{{ dialogError }}</v-alert>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-btn text rounded @click="mergeDialog = false">Cancel</v-btn>
                    <v-btn class="ml-auto" rounded color="primary" :loading="saving" :disabled="!mergeTargetId" @click="mergeConfirmDialog = true">Merge</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <ConfirmDialog :confirmDialog="mergeConfirmDialog" label="Merge feedback into the selected item" :selectedItem="mergeTargetTitle" :loading="saving" @close="mergeConfirmDialog = false" @confirm="confirmMerge" />
    </div>
</template>

<script>
    import ConfirmDialog from "../dialogs/ConfirmDialog.vue"
    import { TYPE_META, STATUS_META, SUITE_LABELS } from "./feedbackMeta"

    export default {
        name: "FeedbackAdminPanel",
        components: { ConfirmDialog },
        data() {
            return {
                items: [],
                loading: false,
                saving: false,
                error: "",
                dialogError: "",
                statusFilter: null,
                target: null,
                rejectDialog: false,
                reason: "",
                editDialog: false,
                edit: { type: "feature", suite: null, title: "", description: "", admin_note: "" },
                mergeDialog: false,
                mergeConfirmDialog: false,
                mergeTargetId: null,
                settableStatuses: ["open", "planned", "in_progress", "done"],
                headers: [
                    { text: "Feedback", value: "title", sortable: false },
                    { text: "Type", value: "type" },
                    { text: "Status", value: "status" },
                    { text: "Company / user", value: "company_name" },
                    { text: "Votes", value: "votes", align: "end" },
                    { text: "Submitted", value: "created_at" },
                    { text: "", value: "actions", sortable: false, align: "end" }
                ],
                auth: "Basic " + window.btoa(process.env.VUE_APP_AUTH)
            }
        },
        computed: {
            statusItems() {
                return Object.keys(STATUS_META).map((value) => ({ value, text: STATUS_META[value].label }))
            },
            typeItems() {
                return Object.keys(TYPE_META).map((value) => ({ value, text: TYPE_META[value].label }))
            },
            suiteItems() {
                return Object.keys(SUITE_LABELS).map((value) => ({ value, text: SUITE_LABELS[value] }))
            },
            filteredItems() {
                return this.statusFilter ? this.items.filter((i) => i.status === this.statusFilter) : this.items
            },
            mergeCandidates() {
                return this.items.filter((i) => this.target && i.id !== this.target.id && !["pending", "merged"].includes(i.status))
            },
            mergeTargetTitle() {
                const found = this.items.find((i) => i.id === this.mergeTargetId)
                return found ? found.title : ""
            }
        },
        methods: {
            typeMeta(type) {
                return TYPE_META[type] || TYPE_META.feature
            },
            statusMeta(status) {
                return STATUS_META[status] || STATUS_META.open
            },
            formatDate(value) {
                const date = new Date(value)
                return isNaN(date.getTime()) ? "" : date.toLocaleDateString()
            },
            body(extra) {
                return Object.assign({ user: localStorage.getItem("user"), company_id: localStorage.getItem("company_id") }, extra || {})
            },
            post(path, extra) {
                return this.$http.post(process.env.VUE_APP_APIURL + path, this.body(extra), { headers: { Authorization: this.auth } })
            },
            errorMessage(error, fallback) {
                return (error && error.body && error.body.error) || fallback
            },
            load() {
                this.loading = true
                this.error = ""
                this.post("/feedback/admin/items")
                    .then((response) => {
                        this.items = response.body.items || []
                        this.$emit("pending-count", response.body.pending || 0)
                    })
                    .catch((error) => {
                        this.error = this.errorMessage(error, "The feedback list could not be loaded.")
                    })
                    .finally(() => {
                        this.loading = false
                    })
            },
            afterChange() {
                this.load()
                this.$emit("changed")
            },
            setStatus(item, status) {
                this.error = ""
                this.post("/feedback/admin/items/" + item.id + "/status", { status })
                    .then(() => this.afterChange())
                    .catch((error) => {
                        this.error = this.errorMessage(error, "The status could not be changed.")
                    })
            },
            openReject(item) {
                this.target = item
                this.reason = ""
                this.dialogError = ""
                this.rejectDialog = true
            },
            confirmReject() {
                this.saving = true
                this.dialogError = ""
                this.post("/feedback/admin/items/" + this.target.id + "/status", { status: "rejected", note: this.reason })
                    .then(() => {
                        this.rejectDialog = false
                        this.afterChange()
                    })
                    .catch((error) => {
                        this.dialogError = this.errorMessage(error, "The item could not be rejected.")
                    })
                    .finally(() => {
                        this.saving = false
                    })
            },
            openEdit(item) {
                this.target = item
                this.edit = { type: item.type, suite: item.suite || null, title: item.title, description: item.description, admin_note: item.admin_note || "" }
                this.dialogError = ""
                this.editDialog = true
            },
            confirmEdit() {
                this.saving = true
                this.dialogError = ""
                this.post("/feedback/admin/items/" + this.target.id + "/update", this.edit)
                    .then(() => {
                        this.editDialog = false
                        this.afterChange()
                    })
                    .catch((error) => {
                        this.dialogError = this.errorMessage(error, "The item could not be saved.")
                    })
                    .finally(() => {
                        this.saving = false
                    })
            },
            openMerge(item) {
                this.target = item
                this.mergeTargetId = null
                this.dialogError = ""
                this.mergeDialog = true
            },
            confirmMerge() {
                this.saving = true
                this.dialogError = ""
                this.post("/feedback/admin/items/" + this.target.id + "/merge", { into_id: this.mergeTargetId })
                    .then(() => {
                        this.mergeConfirmDialog = false
                        this.mergeDialog = false
                        this.afterChange()
                    })
                    .catch((error) => {
                        this.mergeConfirmDialog = false
                        this.dialogError = this.errorMessage(error, "The items could not be merged.")
                    })
                    .finally(() => {
                        this.saving = false
                    })
            }
        },
        mounted() {
            this.load()
        }
    }
</script>
